import React from 'react';
import Error404 from '@/components/Error404';
import { host } from '@/config';
import { clientsUsingSales } from '@/constants/temp_modularization';
import NotAllowed from '@/modules/core/pages/not-allowed';
import { protectedRoute } from '@/modules/core/root/protected/route';
import { queryClient } from '@/query-client';
import { getCmsUserGroups } from '@/services/cms';
import {
  createRoute,
  Outlet,
  redirect,
  useLocation,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import DynamicDownload from 'components/DynamicDownload';
import SectionTitle from 'components/SectionTitle';
import Tabs from 'components/Tabs';
import IntlMessages from 'util/IntlMessages';

import type { chargesSearchSchemaType } from './charges/route';

const SalesErrorComponent = ({ error }: ErrorComponentProps) => {
  if (error instanceof Error && error.message === 'User is not an admin') {
    return <NotAllowed />;
  }

  return <Error404 />;
};

export const salesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'sales',
  component: SalesLayout,
  beforeLoad: ({ context }) => {
    if (!context.auth.userIsAdmin()) {
      throw new Error('User is not an admin');
    }
    if (!clientsUsingSales.includes(host)) {
      throw redirect({ to: '/' });
    }
  },
  errorComponent: SalesErrorComponent,
});

// ! Reminder to redo this after the migration is complete
function SalesLayout() {
  // Prefetch user groups as before
  void queryClient.prefetchQuery({
    queryKey: ['user_groups'],
    queryFn: async () => await getCmsUserGroups(),
    staleTime: Infinity,
  });

  const navigate = salesRoute.useNavigate();
  const location = useLocation();

  const tabs = [
    { label: <IntlMessages id="pages.sales.charges-tab" />, path: 'charges' as const },
    { label: <IntlMessages id="pages.sales.payments-tab" />, path: 'payments' as const },
  ];

  const currentTab = tabs.findIndex((tab) => location.pathname.includes(tab.path));

  const handleTabChange = (index: number) => {
    void navigate({
      to: tabs[index].path,
      search: {} as chargesSearchSchemaType,
    });
  };

  return (
    <>
      <div className="user-admin-view-container h-100">
        <div className="view-section-title mb-4">
          <SectionTitle title={<IntlMessages id="pages.sales.subtitle-1" />} />
        </div>
        <div className="consumption-energy-tabs $tw-mb-6 $tw-flex $tw-gap-4">
          <Tabs
            tabs={tabs}
            selected={currentTab}
            onChange={handleTabChange}
            className="consumption-energy-tabs"
          />
        </div>
        <Outlet />
      </div>
      <DynamicDownload />
    </>
  );
}
